import { ErrorMessage } from '@hookform/error-message'
import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { SSN_LENGTH, SsnInput } from '@components/input/ssn-input'
import { Legend, SubLegend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { useAuthorizedStores } from '@lib/store/useStores'
import type { KycFormValues } from '@lib/utils/kyc'

/**
 * This form is for address validation and autocomplete.
 */
export const EnterLastFourSsn: FunctionComponent<Props> = ({
    title,
    subtitle,
    renderSubmit,
}) => {
    const {
        control,
        watch,
        formState: { errors, submitCount },
    } = useFormContext<KycFormValues>()
    const { kycStore } = useAuthorizedStores()
    const { errorTexts, label, placeholder } = useField(CustomFieldId.Last4ssn)
    const {
        field,
        fieldState: { invalid },
    } = useController({
        control,
        name: 'lastFourSsn',
        rules: {
            required: {
                value: true,
                message: errorTexts.required,
            },
            minLength: {
                value: SSN_LENGTH,
                message: errorTexts.minLength,
            },
            maxLength: {
                value: SSN_LENGTH,
                message: errorTexts.maxLength,
            },
        },
    })

    const lastFourSsn = watch('lastFourSsn')

    useEffect(() => {
        kycStore.setValues({ lastFourSsn })
    }, [lastFourSsn])

    return (
        <>
            <Legend
                preset='default'
                customFieldsetId={CustomFieldsetId.KycEnterLastFourSsn}
            >
                {title}
            </Legend>
            <SubLegend>{subtitle}</SubLegend>

            <SsnInput
                {...field}
                size='medium'
                preset='material'
                validationCount={submitCount}
                valid={!invalid}
                error={<ErrorMessage errors={errors} name='lastFourSsn' />}
                label={label}
                data-testid='kyc.ssn.input'
                placeholder={placeholder}
            />

            {renderSubmit(control)}
        </>
    )
}

type Props = FormProps<KycFormValues>
